import { todayISO as melToday } from "@/lib/intl";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/csvDownload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowDownRight,
  ArrowUpRight,
  AlertTriangle,
  BarChart3,
  Ban,
  CheckCircle2,
  CircleDashed,
  Download,
  Mail,
  RefreshCw,
  StickyNote,
  TrendingDown,
  TrendingUp,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/intl";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RevenueReportTab from "./RevenueReportTab";
import AppointmentCountsReportTab from "./AppointmentCountsReportTab";
import UnpaidReportTab from "./UnpaidReportTab";
import ReferralSourcesReport from "@/components/ReferralSourcesReport";
import GpProviderNumberBackfill from "@/components/GpProviderNumberBackfill";
import { getCached, setCached } from "@/lib/pageStateCache";

const MONTH_LABEL = {
  "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr",
  "05": "May", "06": "Jun", "07": "Jul", "08": "Aug",
  "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec",
};
function formatMonthKey(k) {
  if (!k || k.length < 7) return k || "";
  const y = k.slice(0, 4);
  const m = k.slice(5, 7);
  return `${MONTH_LABEL[m] || m} ${y}`;
}
function formatDate(d) {
  if (!d || d.length < 10) return d || "—";
  return d.split("-").reverse().join("/");
}

function StatCard({ label, value, hint, icon: Icon, accent, testId }) {
  return (
    <Card
      data-testid={testId}
      className={cn(
        "p-6 rounded-lg border shadow-none transition-colors",
        accent === "dark"
          ? "border-zinc-900 bg-zinc-950 text-white"
          : "border-zinc-200 bg-white hover:border-zinc-300"
      )}
    >
      <div className="flex items-start justify-between">
        <div
          className={cn(
            "text-2xs sm:text-xs font-semibold uppercase tracking-[0.2em]",
            accent === "dark" ? "text-zinc-400" : "text-zinc-500"
          )}
        >
          {label}
        </div>
        {Icon && (
          <Icon
            className={cn(
              "h-4 w-4",
              accent === "dark" ? "text-zinc-400" : "text-zinc-400"
            )}
            strokeWidth={1.75}
          />
        )}
      </div>
      <div className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight font-mono-num">
        {value}
      </div>
      {hint && (
        <div
          className={cn(
            "mt-1 text-xs",
            accent === "dark" ? "text-zinc-400" : "text-zinc-500"
          )}
        >
          {hint}
        </div>
      )}
    </Card>
  );
}

function PRESET_OPTIONS(today) {
  const y = today.getFullYear();
  return [
    { value: "last_6m", label: "Last 6 months" },
    { value: "last_12m", label: "Last 12 months" },
    { value: `year_${y}`, label: `Year ${y}` },
    { value: `year_${y - 1}`, label: `Year ${y - 1}` },
    { value: `year_${y - 2}`, label: `Year ${y - 2}` },
  ];
}

export default function Reports() {
  const { status } = useOutletContext();

  const cached = getCached("reports-churn");
  const [period, setPeriod] = useState(cached?.period ?? "last_12m");
  const [gapDays, setGapDays] = useState(cached?.gapDays ?? "90");

  const [data, setData] = useState(cached?.data ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [drillMonth, setDrillMonth] = useState(null);
  const [drillTab, setDrillTab] = useState("acquired");

  const presetOptions = useMemo(() => PRESET_OPTIONS(new Date()), []);

  useEffect(() => {
    setCached("reports-churn", { period, gapDays, data, ts: Date.now() });
  }, [period, gapDays, data]);

  async function fetchReport() {
    if (!status?.configured) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/reports/customers", {
        params: { period, gap_days: Number(gapDays) },
        timeout: 120000,
      });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load report", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Don't auto-fetch — Acuity scans take 10-30s. Wait for the user to
    // click "Run report" after they've chosen the period and gap.
  }, [status?.configured]);

  async function exportFullCsv() {
    try {
      await downloadCsv(
        "/reports/customers/csv",
        { period, gap_days: Number(gapDays) },
        `customer_report_${period}_${data?.period_start}_${data?.period_end}.csv`,
      );
    } catch (e) {
      toast.error("CSV export failed", { description: e.message });
    }
  }

  async function exportMonthCsv() {
    if (!drillMonth || !data) return;
    const grp = data.customers_by_month?.[drillMonth] || {};
    const rows = drillTab === "acquired" ? grp.acquired || [] : grp.churned || [];
    const cols = [
      "name", "email", "first_appt", "last_appt", "total_appts",
      "total_revenue", "last_session_instructor",
    ];
    let csv = cols.join(",") + "\n";
    for (const c of rows) {
      const safe = (v) => {
        const s = String(v ?? "");
        return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      csv += cols.map((k) => safe(c[k])).join(",") + "\n";
    }
    const blob = new Blob([csv], { type: "text/csv" });
    const filename = `customers_${drillTab}_${drillMonth}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("CSV downloaded", { description: filename });
  }

  if (!status?.configured) {
    return (
      <div className="space-y-6" data-testid="reports-page">
        <Card className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
            Connect Acuity to view revenue reports
          </h2>
          <p className="mt-2 text-sm text-zinc-600">
            The Acuity-powered tabs (Acquisition, Revenue, Appointment counts, Unpaid)
            need your Acuity credentials. The Referral Sources and GP Provider Nº
            tabs below work independently — you can use them now.
          </p>
          <Link to="/settings">
            <Button
              className="mt-5 bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="reports-goto-settings"
            >
              Open Settings
            </Button>
          </Link>
        </Card>

        {/* These two work without Acuity — they read non-Acuity data
            (canonical referral registry + GP directory) so we keep
            them reachable even when the Acuity integration isn't
            configured. Rendered as tabs for consistency with the
            main report page. */}
        <Tabs defaultValue="referral-sources" className="w-full">
          <TabsList className="bg-zinc-100 mb-4" data-testid="reports-tabs">
            <TabsTrigger value="referral-sources" data-testid="reports-tab-referral-sources">
              Referral sources
            </TabsTrigger>
            <TabsTrigger value="gp-provnum" data-testid="reports-tab-gp-provnum">
              GP Provider Nos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="referral-sources" className="space-y-8">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                Marketing attribution
              </div>
              <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
                Referral sources
              </h1>
              <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
                Where are new clients hearing about us? Bucketed from the
                standardised "How you heard about us" question on every intake
                form.
              </p>
            </div>
            <ReferralSourcesReport />
          </TabsContent>

          <TabsContent value="gp-provnum" className="space-y-8">
            <GpProviderNumberBackfill />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  const monthly = data?.monthly || [];
  const maxBarVal = Math.max(
    1,
    ...monthly.map((m) => Math.max(m.acquired_count, m.churned_count))
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <Tabs defaultValue="churn" className="w-full">
        <TabsList className="bg-zinc-100 mb-4" data-testid="reports-tabs">
          <TabsTrigger value="churn" data-testid="reports-tab-churn">
            Acquisition vs churn
          </TabsTrigger>
          <TabsTrigger value="revenue" data-testid="reports-tab-revenue">
            Revenue
          </TabsTrigger>
          <TabsTrigger value="appt-counts" data-testid="reports-tab-appt-counts">
            Appointments count
          </TabsTrigger>
          <TabsTrigger value="unpaid" data-testid="reports-tab-unpaid">
            Unpaid appointments
          </TabsTrigger>
          <TabsTrigger value="referral-sources" data-testid="reports-tab-referral-sources">
            Referral sources
          </TabsTrigger>
          <TabsTrigger value="gp-provnum" data-testid="reports-tab-gp-provnum">
            GP Provider Nos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="churn" className="space-y-8">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          Customer health
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
          Acquisition vs. churn
        </h1>
        <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
          New customers each month versus customers who haven't returned in the
          configured gap window. Click any month to inspect the named customers.
        </p>
      </div>

      <Card className="p-5 sm:p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Period
            </Label>
            <div className="mt-1.5">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger
                  className="border-zinc-300 w-full sm:w-56"
                  data-testid="reports-period-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presetOptions.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`reports-period-${o.value}`}
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Churn gap (days)
            </Label>
            <Input
              value={gapDays}
              onChange={(e) => setGapDays(e.target.value)}
              type="number"
              min="1"
              max="730"
              className="border-zinc-300 mt-1.5 w-full sm:w-32 font-mono-num"
              data-testid="reports-gap-input"
            />
          </div>
          <Button
            onClick={fetchReport}
            disabled={loading}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="reports-apply-btn"
          >
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            {loading ? "Running…" : data ? "Re-run report" : "Run report"}
          </Button>
          <Button
            variant="outline"
            className="border-zinc-300 hover:bg-zinc-100 lg:ml-auto"
            disabled={!data || loading}
            onClick={exportFullCsv}
            data-testid="reports-export-csv"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export full CSV
          </Button>
        </div>
        {data && (
          <div
            data-testid="reports-window-info"
            className="mt-3 text-xs text-zinc-500 font-mono-num"
          >
            {data.period_start} → {data.period_end} · gap {data.gap_days}d ·{" "}
            {data.appointments_scanned} appts scanned ·{" "}
            {data.unique_customers_scanned} unique customers
          </div>
        )}
      </Card>

      {error && (
        <div
          data-testid="reports-error"
          className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {!data && !loading && !error && (
        <Card
          data-testid="reports-idle-state"
          className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center"
        >
          <BarChart3 className="h-8 w-8 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
          <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">
            Pick a period and click Run report
          </h2>
          <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto">
            We scan up to 24 months of Acuity history to identify new vs.
            lapsed customers — typically 10–30 seconds depending on the period.
          </p>
        </Card>
      )}

      {loading && !data && (
        <Card
          data-testid="reports-loading-state"
          className="p-12 rounded-lg border border-zinc-200 shadow-none text-center"
        >
          <RefreshCw className="h-8 w-8 text-zinc-400 mx-auto mb-3 animate-spin" strokeWidth={1.5} />
          <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">
            Running report…
          </h2>
          <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto font-mono-num">
            scanning Acuity history · this can take up to 30 seconds
          </p>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Acquired"
              value={data.total_acquired ?? 0}
              hint="New customers in window"
              icon={UserPlus}
              testId="stat-total-acquired"
            />
            <StatCard
              label="Churned"
              value={data.total_churned ?? 0}
              hint={`No bookings for ≥ ${data.gap_days} days`}
              icon={UserMinus}
              testId="stat-total-churned"
            />
            <StatCard
              label="Net change"
              value={`${data.net_change > 0 ? "+" : ""}${data.net_change ?? 0}`}
              hint={
                data.net_change >= 0
                  ? "Growing customer base"
                  : "Losing more than gaining"
              }
              icon={data.net_change >= 0 ? TrendingUp : TrendingDown}
              accent="dark"
              testId="stat-net-change"
            />
          </div>

          <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-zinc-200">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                Monthly breakdown
              </div>
              <p className="mt-1 text-xs text-zinc-500">
                Click a month to view the customer detail list.
              </p>
            </div>
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                      Month
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                      Acquired
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                      Churned
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600 w-[40%]">
                      Visualisation
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                      Net
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthly.map((m) => (
                    <TableRow
                      key={m.month}
                      onClick={() => {
                        setDrillMonth(m.month);
                        setDrillTab(m.acquired_count >= m.churned_count ? "acquired" : "churned");
                      }}
                      className="cursor-pointer"
                      data-testid={`reports-month-row-${m.month}`}
                    >
                      <TableCell className="font-mono-num text-sm text-zinc-900">
                        {formatMonthKey(m.month)}
                      </TableCell>
                      <TableCell className="font-mono-num text-sm text-emerald-700 font-medium">
                        +{m.acquired_count}
                      </TableCell>
                      <TableCell className="font-mono-num text-sm text-rose-700 font-medium">
                        −{m.churned_count}
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="text-2xs text-emerald-700 font-mono-num w-6 shrink-0">
                              {m.acquired_count}
                            </div>
                            <div
                              className="h-2 rounded-sm bg-emerald-500"
                              style={{
                                width: `${(m.acquired_count / maxBarVal) * 100}%`,
                                minWidth: m.acquired_count > 0 ? "2px" : "0",
                              }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-2xs text-rose-700 font-mono-num w-6 shrink-0">
                              {m.churned_count}
                            </div>
                            <div
                              className="h-2 rounded-sm bg-rose-500"
                              style={{
                                width: `${(m.churned_count / maxBarVal) * 100}%`,
                                minWidth: m.churned_count > 0 ? "2px" : "0",
                              }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          "font-mono-num text-sm font-medium",
                          m.net > 0 ? "text-emerald-700"
                            : m.net < 0 ? "text-rose-700"
                            : "text-zinc-500"
                        )}
                      >
                        <span className="inline-flex items-center gap-1">
                          {m.net > 0 ? <ArrowUpRight className="h-3.5 w-3.5" />
                            : m.net < 0 ? <ArrowDownRight className="h-3.5 w-3.5" />
                            : null}
                          {m.net > 0 ? `+${m.net}` : m.net}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {monthly.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">
                        No data in this window
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      {/* Lapsed customers / re-engagement section */}
      <LapsedSection />

      {/* Drill-down dialog */}
      <Dialog open={!!drillMonth} onOpenChange={(o) => !o && setDrillMonth(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {drillMonth ? formatMonthKey(drillMonth) : ""}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Customers acquired or churned in this month. Switch tabs to inspect each list, or export the visible list as CSV.
            </DialogDescription>
          </DialogHeader>
          {drillMonth && data && (
            <DrillBody
              month={drillMonth}
              data={data}
              tab={drillTab}
              setTab={setDrillTab}
              onExport={exportMonthCsv}
            />
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="revenue" className="space-y-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Financial
            </div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
              Revenue
            </h1>
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
              Pick a source, breakdown and period to chart and tabulate revenue.
              Rolling monthly or weekly windows, plus year-over-year and same-month comparisons.
            </p>
          </div>
          <RevenueReportTab />
        </TabsContent>

        <TabsContent value="appt-counts" className="space-y-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Operations
            </div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
              Appointments count
            </h1>
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
              How many appointments per day, week or month — optionally sliced by calendar,
              appointment type or status. Complements the revenue view when you want to know
              <em> how many </em>rather than<em> how much</em>.
            </p>
          </div>
          <AppointmentCountsReportTab />
        </TabsContent>

        <TabsContent value="unpaid" className="space-y-8">
          <UnpaidReportTab />
        </TabsContent>

        <TabsContent value="referral-sources" className="space-y-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Marketing attribution
            </div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
              Referral sources
            </h1>
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
              Where are new clients hearing about us? Bucketed from the
              standardised "How you heard about us" question on every intake
              form.
            </p>
          </div>
          <ReferralSourcesReport />
        </TabsContent>

        <TabsContent value="gp-provnum" className="space-y-8">
          <GpProviderNumberBackfill />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DrillBody({ month, data, tab, setTab, onExport }) {
  const grp = data.customers_by_month?.[month] || { acquired: [], churned: [] };
  const rows = tab === "acquired" ? grp.acquired : grp.churned;
  const totalRevenue = rows.reduce((a, r) => a + Number(r.total_revenue || 0), 0);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex items-center gap-2 mb-4">
        <Button
          size="sm"
          variant={tab === "acquired" ? "default" : "outline"}
          onClick={() => setTab("acquired")}
          className={cn(
            tab === "acquired"
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "border-zinc-300 hover:bg-zinc-100"
          )}
          data-testid="drill-tab-acquired"
        >
          <UserPlus className="h-3.5 w-3.5 mr-1.5" />
          Acquired ({grp.acquired.length})
        </Button>
        <Button
          size="sm"
          variant={tab === "churned" ? "default" : "outline"}
          onClick={() => setTab("churned")}
          className={cn(
            tab === "churned"
              ? "bg-rose-600 hover:bg-rose-700 text-white"
              : "border-zinc-300 hover:bg-zinc-100"
          )}
          data-testid="drill-tab-churned"
        >
          <UserMinus className="h-3.5 w-3.5 mr-1.5" />
          Churned ({grp.churned.length})
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-zinc-300 hover:bg-zinc-100 ml-auto"
          onClick={onExport}
          disabled={rows.length === 0}
          data-testid="drill-export-csv"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Export {tab} CSV
        </Button>
      </div>

      <div className="text-xs text-zinc-500 font-mono-num mb-2">
        {rows.length} customer{rows.length === 1 ? "" : "s"} ·{" "}
        Total revenue {fmtMoney(totalRevenue)}
      </div>

      <div className="flex-1 overflow-auto thin-scroll border border-zinc-200 rounded-md">
        <Table>
          <TableHeader className="sticky top-0 bg-zinc-50 z-10">
            <TableRow>
              {[
                "Name", "Email", "First appt", "Last appt",
                "Appts", "Revenue", "Last instructor",
              ].map((h) => (
                <TableHead
                  key={h}
                  className="text-xs font-semibold uppercase tracking-wider text-zinc-600"
                >
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">
                  No {tab} customers this month
                </TableCell>
              </TableRow>
            )}
            {rows.map((c, i) => (
              <TableRow key={`${c.email || c.name}-${i}`} data-testid={`drill-row-${i}`}>
                <TableCell className="text-sm text-zinc-950 font-medium">{c.name}</TableCell>
                <TableCell className="text-sm text-zinc-700">{c.email || "—"}</TableCell>
                <TableCell className="font-mono-num text-xs text-zinc-700">{formatDate(c.first_appt)}</TableCell>
                <TableCell className="font-mono-num text-xs text-zinc-700">{formatDate(c.last_appt)}</TableCell>
                <TableCell className="font-mono-num text-sm text-zinc-900">{c.total_appts}</TableCell>
                <TableCell className="font-mono-num text-sm text-zinc-900">{fmtMoney(c.total_revenue)}</TableCell>
                <TableCell className="text-sm text-zinc-700">{c.last_session_instructor || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function LapsedSection() {
  const [minDays, setMinDays] = useState("30");
  const [maxDays, setMaxDays] = useState("90");
  const [excludeWithin, setExcludeWithin] = useState("30");
  const [includeDnc, setIncludeDnc] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);

  function lapsedQueryParams() {
    const ew = Number(excludeWithin);
    const params = { min_days: Number(minDays), max_days: Number(maxDays) };
    if (Number.isFinite(ew) && ew > 0) params.exclude_contacted_within = ew;
    if (includeDnc) params.include_dnc = true;
    return params;
  }

  async function runLapsed() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/reports/lapsed", {
        params: lapsedQueryParams(),
        timeout: 120000,
      });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load lapsed list", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  async function exportFull() {
    try {
      await downloadCsv(
        "/reports/lapsed/csv",
        { ...lapsedQueryParams(), kind: "full" },
        `reengagement_full_${minDays}_${maxDays}d.csv`,
      );
    } catch (e) {
      toast.error("CSV export failed", { description: e.message });
    }
  }

  async function exportEmails() {
    try {
      await downloadCsv(
        "/reports/lapsed/csv",
        { ...lapsedQueryParams(), kind: "emails" },
        `reengagement_emails_${minDays}_${maxDays}d.csv`,
      );
    } catch (e) {
      toast.error("Email list export failed", { description: e.message });
    }
  }

  async function bulkMarkContactedToday() {
    if (!data?.customers?.length) return;
    setMarking(true);
    try {
      const customers = data.customers.map((c) => ({ email: c.email, name: c.name }));
      const r = await api.post("/customer-contacts/bulk-mark-contacted", { customers });
      toast.success(`Marked ${r.data.marked} as contacted today`, {
        description: r.data.date,
      });
      await runLapsed();
    } catch (e) {
      toast.error("Bulk mark failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setMarking(false);
    }
  }

  async function patchContact(customer, patch) {
    try {
      await api.post("/customer-contacts", { customer, ...patch });
      // Optimistic local update so the row reflects immediately
      setData((d) => {
        if (!d) return d;
        const ck = (a, b) => {
          const ae = (a.email || "").toLowerCase();
          const be = (b.email || "").toLowerCase();
          if (ae && be) return ae === be;
          return (a.name || "").toLowerCase() === (b.name || "").toLowerCase();
        };
        return {
          ...d,
          customers: d.customers.map((row) => {
            if (!ck(row, customer)) return row;
            const updated = { ...row };
            if (patch.set_last_contacted) {
              updated.last_contacted = patch.last_contacted || null;
              if (patch.last_contacted) {
                const today = new Date(d.today);
                const lc = new Date(patch.last_contacted);
                updated.days_since_contact = Math.round((today - lc) / 86400000);
              } else {
                updated.days_since_contact = null;
              }
            }
            if (patch.set_comment) {
              updated.comment = patch.comment || null;
            }
            if (patch.set_do_not_contact) {
              updated.do_not_contact = !!patch.do_not_contact;
            }
            return updated;
          }),
        };
      });
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function copyEmailsToClipboard() {
    if (!data?.customers?.length) return;
    const seen = new Set();
    const emails = [];
    for (const c of data.customers) {
      const e = (c.email || "").trim().toLowerCase();
      if (e && !seen.has(e)) {
        seen.add(e);
        emails.push(e);
      }
    }
    try {
      await navigator.clipboard.writeText(emails.join("\n"));
      toast.success(`${emails.length} email${emails.length === 1 ? "" : "s"} copied`, {
        description: "Paste into Mailchimp, Campaign Monitor, etc.",
      });
    } catch (e) {
      toast.error("Copy failed", { description: e.message });
    }
  }

  return (
    <Card className="rounded-lg border border-zinc-200 shadow-none" data-testid="lapsed-section">
      <div className="p-5 sm:p-6 border-b border-zinc-200 flex flex-col sm:flex-row sm:items-end gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Lapsed customers · re-engagement
          </div>
          <h2 className="mt-1 font-display text-2xl tracking-tight text-zinc-950">
            Win-back list
          </h2>
          <p className="mt-1 text-xs text-zinc-600 max-w-xl">
            Customers whose last appointment was between the configured days
            ago — trending toward churn but still active enough to win back.
          </p>
        </div>
        <div className="sm:ml-auto flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              From (days)
            </Label>
            <Input
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
              type="number"
              min="0"
              max="730"
              className="border-zinc-300 mt-1.5 w-24 font-mono-num"
              data-testid="lapsed-min-days"
            />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              To (days)
            </Label>
            <Input
              value={maxDays}
              onChange={(e) => setMaxDays(e.target.value)}
              type="number"
              min="1"
              max="730"
              className="border-zinc-300 mt-1.5 w-24 font-mono-num"
              data-testid="lapsed-max-days"
            />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Hide if contacted in (days)
            </Label>
            <Input
              value={excludeWithin}
              onChange={(e) => setExcludeWithin(e.target.value)}
              type="number"
              min="0"
              max="730"
              className="border-zinc-300 mt-1.5 w-32 font-mono-num"
              data-testid="lapsed-exclude-within"
              placeholder="0 = off"
            />
          </div>
          <label
            className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer select-none mb-1.5"
            data-testid="lapsed-include-dnc-label"
          >
            <input
              type="checkbox"
              checked={includeDnc}
              onChange={(e) => setIncludeDnc(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
              data-testid="lapsed-include-dnc"
            />
            Show do-not-contact
          </label>
          <Button
            onClick={runLapsed}
            disabled={loading}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="lapsed-run-btn"
          >
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            {loading ? "Running…" : data ? "Re-run" : "Run"}
          </Button>
        </div>
      </div>

      {error && (
        <div
          data-testid="lapsed-error"
          className="m-5 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {!data && !loading && !error && (
        <div className="p-8 text-center text-sm text-zinc-500" data-testid="lapsed-idle">
          Choose a window and click Run to find lapsed customers.
        </div>
      )}

      {loading && !data && (
        <div className="p-8 text-center text-sm text-zinc-500 font-mono-num">
          scanning history · 10–30 seconds
        </div>
      )}

      {data && (
        <>
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500">
                Lapsed customers
              </div>
              <div className="mt-1 font-mono-num text-2xl text-zinc-950">
                {data.count}
              </div>
              <div className="text-xs text-zinc-500 font-mono-num mt-0.5">
                last seen {data.min_days}–{data.max_days} days ago
              </div>
            </div>
            <div>
              <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500">
                Reachable by email
              </div>
              <div className="mt-1 font-mono-num text-2xl text-zinc-950">
                {data.with_email_count}
              </div>
              <div className="text-xs text-zinc-500 font-mono-num mt-0.5">
                of {data.count} ·{" "}
                {data.count > 0
                  ? `${Math.round((data.with_email_count / data.count) * 100)}%`
                  : "—"}
                {data.dnc_visible_count > 0 && (
                  <span className="text-rose-600">
                    {" "}· {data.dnc_visible_count} DNC excluded
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500">
                Revenue at risk
              </div>
              <div className="mt-1 font-mono-num text-2xl text-zinc-950">
                {fmtMoney(data.total_revenue_at_risk)}
              </div>
              <div className="text-xs text-zinc-500 font-mono-num mt-0.5">
                lifetime revenue from this cohort
              </div>
            </div>
          </div>

          <div className="px-5 sm:px-6 py-3 border-b border-zinc-200 flex flex-wrap gap-2 bg-zinc-50">
            <Button
              size="sm"
              onClick={copyEmailsToClipboard}
              disabled={!data.with_email_count}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="lapsed-copy-emails-btn"
            >
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              Copy {data.with_email_count} emails
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={exportEmails}
              disabled={!data.with_email_count}
              className="border-zinc-300 hover:bg-zinc-100"
              data-testid="lapsed-export-emails-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Email list CSV
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={exportFull}
              disabled={!data.count}
              className="border-zinc-300 hover:bg-zinc-100"
              data-testid="lapsed-export-full-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Full details CSV
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={bulkMarkContactedToday}
              disabled={!data.count || marking}
              className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
              data-testid="lapsed-bulk-mark-btn"
              title="Records today's date as last_contacted for every visible customer"
            >
              <CheckCircle2 className={cn("h-3.5 w-3.5 mr-1.5", marking && "animate-pulse")} />
              {marking ? "Marking…" : `Mark ${data.count} as contacted today`}
            </Button>
            <span className="ml-auto text-xs text-zinc-500 font-mono-num self-center">
              sorted by days since last appt
            </span>
          </div>

          <div className="overflow-x-auto thin-scroll max-h-[600px]">
            <Table>
              <TableHeader className="sticky top-0 bg-zinc-50 z-10">
                <TableRow>
                  {[
                    "Days", "Name", "Email", "Last appt", "Appts", "Revenue",
                    "Last contacted", "Comment", "DNC",
                  ].map((h) => (
                    <TableHead
                      key={h}
                      className="text-xs font-semibold uppercase tracking-wider text-zinc-600"
                    >
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.customers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="py-12 text-center text-sm text-zinc-500">
                      No lapsed customers in this window — nice work!
                    </TableCell>
                  </TableRow>
                )}
                {data.customers.map((c, i) => (
                  <TableRow
                    key={`${c.email || c.name}-${i}`}
                    data-testid={`lapsed-row-${i}`}
                    className={cn(c.do_not_contact && "bg-zinc-50/70 opacity-70")}
                  >
                    <TableCell className={cn(
                      "font-mono-num text-sm font-medium",
                      c.do_not_contact ? "text-zinc-500 line-through" : "text-amber-700",
                    )}>
                      {c.days_since_last}d
                    </TableCell>
                    <TableCell className={cn(
                      "text-sm font-medium",
                      c.do_not_contact ? "text-zinc-500" : "text-zinc-950",
                    )}>
                      <div className={cn(c.do_not_contact && "line-through")}>{c.name}</div>
                      {c.last_session_instructor && (
                        <div className="text-2xs text-zinc-400 font-normal mt-0.5">
                          {c.last_session_instructor}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-700">
                      {c.email ? (
                        c.do_not_contact ? (
                          <span className="text-zinc-500 line-through">{c.email}</span>
                        ) : (
                          <a
                            href={`mailto:${c.email}`}
                            className="text-zinc-700 hover:text-zinc-950 underline-offset-2 hover:underline"
                          >
                            {c.email}
                          </a>
                        )
                      ) : (
                        <span className="text-zinc-400 italic text-xs">no email</span>
                      )}
                      {c.phone && (
                        <div className="text-2xs text-zinc-400 font-mono-num mt-0.5">
                          {c.phone}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-num text-xs text-zinc-700">
                      {formatDate(c.last_appt)}
                    </TableCell>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {c.total_appts}
                    </TableCell>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {fmtMoney(c.total_revenue)}
                    </TableCell>
                    <TableCell>
                      <ContactedCell
                        customer={c}
                        index={i}
                        onSave={(patch) => patchContact({ email: c.email, name: c.name }, patch)}
                      />
                    </TableCell>
                    <TableCell>
                      <CommentCell
                        customer={c}
                        index={i}
                        onSave={(patch) => patchContact({ email: c.email, name: c.name }, patch)}
                      />
                    </TableCell>
                    <TableCell>
                      <DncCell
                        customer={c}
                        index={i}
                        onSave={(patch) => patchContact({ email: c.email, name: c.name }, patch)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </Card>
  );
}

// ---------- Inline editor cells ----------
function ContactedCell({ customer, index, onSave }) {
  const [open, setOpen] = useState(false);
  const todayIso = melToday();
  const [draft, setDraft] = useState(customer.last_contacted || todayIso);

  useEffect(() => {
    setDraft(customer.last_contacted || todayIso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.last_contacted]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "text-xs font-mono-num inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-colors",
            customer.last_contacted
              ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
              : "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50"
          )}
          data-testid={`lapsed-contacted-cell-${index}`}
        >
          {customer.last_contacted ? (
            <>
              <CheckCircle2 className="h-3 w-3" />
              {formatDate(customer.last_contacted)}
              {customer.days_since_contact != null && (
                <span className="text-2xs text-emerald-600">
                  · {customer.days_since_contact}d ago
                </span>
              )}
            </>
          ) : (
            <>
              <CircleDashed className="h-3 w-3" />
              Mark contacted
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <div className="space-y-3">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Last contacted date
            </Label>
            <Input
              type="date"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="border-zinc-300 mt-1.5 font-mono-num"
              data-testid={`lapsed-contacted-input-${index}`}
            />
          </div>
          <div className="flex justify-between items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-rose-700 border-rose-200 hover:bg-rose-50"
              disabled={!customer.last_contacted}
              onClick={async () => {
                await onSave({
                  set_last_contacted: true,
                  last_contacted: null,
                  set_comment: false,
                });
                setOpen(false);
              }}
              data-testid={`lapsed-contacted-clear-${index}`}
            >
              Clear
            </Button>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="text-zinc-600"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
                onClick={async () => {
                  await onSave({
                    set_last_contacted: true,
                    last_contacted: draft,
                    set_comment: false,
                  });
                  setOpen(false);
                }}
                data-testid={`lapsed-contacted-save-${index}`}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CommentCell({ customer, index, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(customer.comment || "");

  useEffect(() => {
    setDraft(customer.comment || "");
  }, [customer.comment]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-colors max-w-[200px] text-left",
            customer.comment
              ? "border-zinc-300 bg-amber-50/50 text-zinc-800 hover:bg-amber-50"
              : "border-zinc-200 bg-white text-zinc-400 hover:bg-zinc-50 italic"
          )}
          title={customer.comment || "Add note"}
          data-testid={`lapsed-comment-cell-${index}`}
        >
          <StickyNote className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {customer.comment || "Add note"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="space-y-3">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Note for {customer.name}
            </Label>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. Travelling overseas — back in June"
              className="w-full mt-1.5 px-3 py-2 text-sm rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-500 min-h-[80px] resize-none"
              data-testid={`lapsed-comment-input-${index}`}
            />
            <p className="text-2xs text-zinc-500 mt-1">
              Use for known reasons — sick, travel, on a break, etc.
            </p>
          </div>
          <div className="flex justify-between items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-rose-700 border-rose-200 hover:bg-rose-50"
              disabled={!customer.comment}
              onClick={async () => {
                setDraft("");
                await onSave({
                  set_comment: true,
                  comment: null,
                  set_last_contacted: false,
                });
                setOpen(false);
              }}
            >
              Clear
            </Button>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(customer.comment || "");
                  setOpen(false);
                }}
                className="text-zinc-600"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
                onClick={async () => {
                  await onSave({
                    set_comment: true,
                    comment: draft.trim() || null,
                    set_last_contacted: false,
                  });
                  setOpen(false);
                }}
                data-testid={`lapsed-comment-save-${index}`}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DncCell({ customer, index, onSave }) {
  const dnc = !!customer.do_not_contact;
  const [busy, setBusy] = useState(false);

  async function toggle() {
    if (dnc) {
      setBusy(true);
      try {
        await onSave({
          set_do_not_contact: true,
          do_not_contact: false,
          set_last_contacted: false,
          set_comment: false,
        });
      } finally {
        setBusy(false);
      }
      return;
    }
    const ok = window.confirm(
      `Mark ${customer.name || customer.email} as do-not-contact?\n\n` +
      "They'll be hidden from the win-back list and always excluded from email exports. " +
      "You can undo this any time."
    );
    if (!ok) return;
    setBusy(true);
    try {
      await onSave({
        set_do_not_contact: true,
        do_not_contact: true,
        set_last_contacted: false,
        set_comment: false,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={cn(
        "text-2xs inline-flex items-center gap-1 px-2 py-1 rounded border transition-colors",
        dnc
          ? "border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 font-medium"
          : "border-zinc-200 bg-white text-zinc-400 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200",
        busy && "opacity-50 cursor-wait",
      )}
      title={
        dnc
          ? "Click to unset do-not-contact"
          : "Mark as do-not-contact (won't appear in win-back list or email exports)"
      }
      data-testid={`lapsed-dnc-cell-${index}`}
    >
      <Ban className="h-3 w-3" />
      {dnc ? "DNC" : "Mark"}
    </button>
  );
}

